<template>
  <div>
    <div class="share-receive">
      <div class="title">{{ c2cConfig.skinInfo.dialogPrizeTitle }}</div>
      <div class="desc">{{ c2cConfig.skinInfo.dialogPrizeDesc }}</div>
      <div class="main">
        <!-- 券列表 -->
        <CouponListSkeleton :isReady="!!c2cCouponList.length">
          <CouponList :c2cCouponList="c2cCouponList" @onOpenCouponDetail="onOpenCouponDetail"></CouponList>
        </CouponListSkeleton>
        <!-- 分享按钮 -->
        <FitButton
          v-if="c2cCouponList.length && isAllExchanged"
          class="btn"
          @click="goToCouponList"
          v-stat.brow.click="{ tag: 'c2c.view_btn' }"
        >
          {{ i18n('查看已領優惠') }}
        </FitButton>
        <!-- 分享按钮 -->
        <FitButton
          v-else
          class="btn"
          @click="onShare(C2CSharerAction.share)"
          v-stat.brow.click="{ tag: 'c2c.share_btn' }"
        >
          {{ i18n('立即邀請') }}
        </FitButton>
        <!-- 分享链接 -->
        <div class="link" @click="onShare(C2CSharerAction.copy)" v-stat.brow.click="{ tag: 'c2c.copy_btn' }">
          {{ i18n('複製分享鏈接') }}
        </div>
      </div>
    </div>
    <!-- 微信分享浮窗 -->
    <WxShare ref="wxShareDialog"></WxShare>
    <!-- 券详情弹窗 -->
    <CouponDetail
      class="c2c-coupon-dialog"
      ref="couponDetail"
      :couponInfo="currentCouponItem?.couponInfo"
      :extraParams="{ isShowTips: false, tipsTextRight: '', prizeDesc: '' }"
      :btnInfo="currentCouponItem?.btnInfo"
      :titleText="i18n('確認領取')"
      @onBtnClick="onBtnClick"
    >
      <template slot="underBtn">
        <div class="c2c-coupon-dialog under-btn" v-stat.brow="{ tag: 'c2c.coupon_dialog_brow', ext: dialogExt }">
          <span v-if="currentCouponItem?.dialogStatus === C2CDialogCouponState.locked">
            {{
              currentCouponItem.prizeInfo.threshold > 1
                ? i18n('累計成功邀請X人後可拎', { sum: currentCouponItem.prizeInfo.threshold })
                : i18n('累計成功邀請X人後可拎1', { sum: currentCouponItem.prizeInfo.threshold })
            }}
          </span>
          <span v-else-if="currentCouponItem?.dialogStatus === C2CDialogCouponState.limit">
            {{ i18n('今日已領完，明日再來拎！') }}
          </span>
        </div>
      </template>
    </CouponDetail>
  </div>
</template>

<script lang="ts" setup>
import { biClick } from '@tencent/fit-bi-sdk';
import { CustomException } from '@tencent/ppd-base-libs';
import { CouponDetail } from '@tencent/wphk-act-base-components';
import { CoreFormatCoupon } from '@tencent/wphk-act-base-framework';
import { copy } from '@tencent/wphk-components-copy';
import { showBusinessError } from '@tencent/wphk-components-dialog';
import { jumpPage } from '@tencent/wphk-components-jump-page';
import { hideLoading, showLoading } from '@tencent/wphk-components-loading';
import { FitButton } from '@tencent/wphk-ui';
import { computed, nextTick, ref } from 'vue-demi';
import { getWxShareInfo, handleSetWxShareInfo } from '../../business';
import { useActInfo } from '../../hooks/use-act-info';
import { useHighLogin } from '../../hooks/use-high-login';
import { i18n } from '../../i18n';
import { C2CConfig, C2CCoupon, C2CDialogCouponState, C2CSharerAction, C2CShareType, WxShareInfo } from '../../types';
import CouponList from './coupon-list.vue';
import CouponListSkeleton from './coupon-list-skeleton.vue';
import WxShare from './wx-share.vue';

const props = defineProps<{
  c2cConfig: C2CConfig;
}>();
defineExpose({ onShare });

const wxShareDialog = ref<InstanceType<typeof WxShare> | null>(null);

const couponDetail = ref<InstanceType<typeof CouponDetail> | null>(null);

const shareInfo = ref<WxShareInfo>();

const { checkHighAndLogin } = useHighLogin();

const { c2cCouponList, exchangeCoupon, isAllExchanged } = useActInfo();

const currentCouponItem = ref<C2CCoupon>();

const isExchanging = ref(false);

/** 立即邀請 */
async function onShare(sharerAction = C2CSharerAction.share) {
  try {
    if (!shareInfo.value) {
      await checkHighAndLogin({ shareType: C2CShareType.C2CSharer, sharerAction });
      shareInfo.value = await getWxShareInfo(props.c2cConfig.actInfo.actId);
    }
    if (sharerAction === C2CSharerAction.share) {
      handleSetWxShareInfo({
        link: shareInfo.value.wxShareUrl,
        shareInfo: props.c2cConfig.shareInfo,
      });
      wxShareDialog.value?.showDialog();
    }
    if (sharerAction === C2CSharerAction.copy) {
      // 全部已兑换时候，复制链接时候，默认设置一次微信分享
      // 没有微信分享链接缓存&&没有邀请按钮时候，点击复制链接后直接进行微信分享,如果没有设置就无法分享了
      if (isAllExchanged.value) {
        handleSetWxShareInfo({
          link: shareInfo.value.wxShareUrl,
          shareInfo: props.c2cConfig.shareInfo,
        });
      }
      setTimeout(() => {
        copy(
          {
            text: `${props.c2cConfig.shareInfo.copyContent}${shareInfo.value?.shortUrlForCopy}`,
            fail: (err) => {
              new CustomException(err, 'c2cCopyError', '复制失败');
            },
          },
          '',
        );
      }, 100);
    }
  } catch (error) {
    showBusinessError(error);
  }
}

function onOpenCouponDetail(item: C2CCoupon) {
  currentCouponItem.value = item;
  nextTick(() => {
    couponDetail.value?.showDialog();
  });
}

async function handleExchangeCoupon() {
  const prizeInfo = currentCouponItem.value?.prizeInfo;
  if (!prizeInfo) return;
  if (isExchanging.value) return;
  isExchanging.value = true;
  showLoading();
  couponDetail.value?.hideDialog();
  const { prizeId, exchangeActivityId, exchangePrizeId } = prizeInfo;
  await exchangeCoupon(props.c2cConfig.actInfo.actId, prizeId, exchangeActivityId, exchangePrizeId);
  hideLoading();
  isExchanging.value = false;
}

const dialogExt = computed(() => ({
  fcouponId: currentCouponItem.value?.couponInfo.couponId || '',
  fdialogStatus: currentCouponItem.value?.dialogStatus || '',
}));

async function onBtnClick() {
  biClick('c2c.coupon_dialog_btn', dialogExt.value);
  // 今日已领完，待解锁
  if (currentCouponItem.value?.btnInfo.isDisabled) return;
  // 去使用
  if (currentCouponItem.value?.dialogStatus === C2CDialogCouponState.received) {
    const { userCouponid } = currentCouponItem.value.couponInfo;
    if (userCouponid) {
      if (CoreFormatCoupon.isCpType(currentCouponItem.value)) {
        jumpPage.goCouponDetail({ userScid: userCouponid, adtag: 'shareCoupon' });
      } else {
        jumpPage.goMerchantCouponDetail({ userScid: userCouponid, adtag: 'shareCoupon' });
      }
    }
    new CustomException(userCouponid, 'goUseByEmptyUserscid', '去使用没有用户券 id');
    return;
  }
  // 立即領取
  handleExchangeCoupon();
}

function goToCouponList() {
  jumpPage.goMyCoupons({ adtag: 'c2c' });
}
</script>

<style lang="less">
@import (reference) '~@/less/main';
.c2c-coupon-dialog.under-btn {
  .font-24-regular(@black_50);
  text-align: center;
  width: 100%;
  margin-top: 8 / @rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
<style lang="less" scoped>
@import (reference) '~@/less/main';
.share-receive {
  background-color: #fff;
  border-radius: 24 / @rem;
  padding: 24 / @rem 32 / @rem 40 / @rem 32 / @rem;
  margin-bottom: 16 / @rem;
  .title {
    .font-32-medium(@black_90);
  }
  .desc {
    .font-24-regular(@black_50);
    margin-top: 4 / @rem;
  }
  .main {
    display: flex;
    align-items: center;
    flex-direction: column;
    .btn {
      min-width: 368 / @rem;
      width: 368 / @rem;
      margin-bottom: 24 / @rem;
    }
    .link {
      .font-24-regular(@blue_02);
    }
  }
}
</style>
